import { useState, useEffect } from 'react'
import { Box, Typography } from '@mui/material'
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'

import type { AvailablePhysicianSlot } from '../AppointmentService'

type CustomAppointmentProps = {
  setSlot: (value: AvailablePhysicianSlot) => void
  date: Date
  // The by-provider tab derives this from its chosen visit reason, which is nullable there.
  reason: string | null
  physicianId: number
  setIsCustomAppointment: (value: boolean) => void
}

const CustomAppointment = ({
  setSlot,
  date,
  reason,
  physicianId,
  setIsCustomAppointment,
}: CustomAppointmentProps) => {
  const [value, setValue] = useState<Date | null>(null)

  const handleChange = (newValue: Date | null) => {
    setValue(newValue)
    setIsCustomAppointment(true)
    if (!newValue) {
      return
    }
    // Extract hours and minutes from the selected time
    const hours = newValue.getHours()
    const minutes = newValue.getMinutes()

    // Create a new Date object for the selected date with the chosen time in local timezone
    // This ensures we use the correct DST offset for the appointment date, not today's offset
    const appointmentDateTime = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      hours,
      minutes,
      0,
      0,
    )

    // Convert to standard ISO 8601 format (YYYY-MM-DDTHH:MM:SS.sssZ)
    const scheduledDate = appointmentDateTime.toISOString()

    setSlot({
      scheduled_date: scheduledDate,
      physician_id: physicianId,
      reason: reason ?? '',
    })
  }

  // A new date or physician clears the custom time; the parent's flag is its own state, so an
  // effect resets it.
  const [timeSetFor, setTimeSetFor] = useState({ date, physicianId })
  if (date !== timeSetFor.date || physicianId !== timeSetFor.physicianId) {
    setTimeSetFor({ date, physicianId })
    setValue(null)
  }
  useEffect(() => {
    setIsCustomAppointment(false)
  }, [date, physicianId, setIsCustomAppointment])

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        marginBottom: 6,
        marginTop: 4,
        rowGap: 1,
      }}
    >
      <Typography variant="body2" sx={{ mr: 2 }}>
        Create Custom Appointment
      </Typography>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <TimePicker
          value={value}
          onChange={handleChange}
          // Configured via slotProps rather than a `slots.textField` render
          // function: `slots` entries are component *types*, so an inline arrow
          // is a new type every render and React remounts the field. That
          // detaches the node the popper is anchored to, and the open clock
          // jumps to the top-left corner the first time the value changes.
          slotProps={{
            textField: {
              variant: 'outlined',
              size: 'small',
              // v9 renders the field with its own PickersTextField: the input is a nested
              // slot rather than MUI TextField's `InputProps`, and `placeholder` belongs
              // on the underlying html input.
              slotProps: {
                input: {
                  sx: { flexDirection: 'row-reverse' },
                },
                htmlInput: {
                  placeholder: 'Select time',
                },
              },
            },
          }}
        />
      </LocalizationProvider>
    </Box>
  )
}

export default CustomAppointment
